import { App as AntApp, Button, Popconfirm, Space, Spin, Tag, Typography } from "antd";
import { CheckOutlined, DisconnectOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { ApiError } from "../app/apiError.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { useConfirmTelegramLink, useTelegramLinks, useUnlinkTelegram, type TelegramLink, type TelegramProfileKind } from "../app/telegram.ts";

interface Props {
  /** The person's active links, or undefined while they load. */
  links: TelegramLink[] | undefined;
  loading: boolean;
  failed: boolean;
  /** Shown in the unlink button's accessible name and confirmation. */
  name: string;
  /** people.manage: may unlink, and confirm a link flagged for review. */
  canUnlink: boolean;
}

/**
 * Whether a person's Telegram account is linked, with an unlink button for staff who manage people. There is no manual
 * link: the person links themselves by sending /start to the bot and sharing their own phone, which the hint explains.
 * A link made from a phone that has since been removed from the profile is flagged "needs review" (bd-3qg.17): staff
 * confirm it if the account still belongs to the person, or unlink it.
 */
export function TelegramLinkState({ links, loading, failed, name, canUnlink }: Props) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const unlink = useUnlinkTelegram();
  const confirm = useConfirmTelegramLink();
  const showError = (err: unknown) => void message.error((err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]).join(" "));

  if (loading) return <Spin size="small" aria-label={t("app.loading")} />;
  if (failed) return <Typography.Text type="danger">{t("telegram.loadError")}</Typography.Text>;

  if (!links || links.length === 0) {
    return (
      <Space orientation="vertical" size={2}>
        <Tag>{t("telegram.notLinked")}</Tag>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t("telegram.hint")}
        </Typography.Text>
      </Space>
    );
  }

  return (
    <Space orientation="vertical" size={4}>
      {links.map((link) => (
        <Space key={link.id} orientation="vertical" size={2}>
          <Space wrap size={4}>
            {link.needsReviewSince ? <Tag color="orange">{t("telegram.needsReview")}</Tag> : <Tag color="green">{t("telegram.linked")}</Tag>}
            <Typography.Text>{link.telegramUsername ? `@${link.telegramUsername}` : t("telegram.accountId", { id: String(link.telegramUserId) })}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t("telegram.linkedAt", { time: formatSchoolDateTime(link.linkedAt) })}
            </Typography.Text>
            {canUnlink && link.needsReviewSince && (
              <Button
                size="small"
                type="link"
                icon={<CheckOutlined />}
                loading={confirm.isPending && confirm.variables === link.id}
                aria-label={t("telegram.confirmNamed", { name })}
                onClick={() => confirm.mutate(link.id, { onSuccess: () => void message.success(t("telegram.confirmed", { name })), onError: showError })}
              >
                {t("telegram.confirm")}
              </Button>
            )}
            {canUnlink && (
              <Popconfirm
                title={t("telegram.unlink")}
                description={<div style={{ maxWidth: 320 }}>{t("telegram.unlinkConfirm", { name })}</div>}
                okText={t("telegram.unlink")}
                cancelText={t("common.cancel")}
                okButtonProps={{ danger: true }}
                onConfirm={() => unlink.mutate(link.id, { onSuccess: () => void message.success(t("telegram.unlinked", { name })), onError: showError })}
              >
                <Button size="small" type="link" danger icon={<DisconnectOutlined />} loading={unlink.isPending && unlink.variables === link.id} aria-label={t("telegram.unlinkNamed", { name })}>
                  {t("telegram.unlink")}
                </Button>
              </Popconfirm>
            )}
          </Space>
          {link.needsReviewSince && (
            <Typography.Text type="warning" style={{ fontSize: 12, maxWidth: 420, display: "block" }}>
              {link.sourcePhone ? t("telegram.reviewHint", { phone: link.sourcePhone }) : t("telegram.reviewHintNoPhone")}
            </Typography.Text>
          )}
        </Space>
      ))}
    </Space>
  );
}

/** The link state of one teacher or staff profile, fetched on its own (used in the profile drawers). */
export function ProfileTelegramLink({ kind, profileId, name, canUnlink }: { kind: TelegramProfileKind; profileId: string; name: string; canUnlink: boolean }) {
  const query = useTelegramLinks(kind, [profileId]);
  return <TelegramLinkState links={query.data} loading={query.isPending} failed={query.isError} name={name} canUnlink={canUnlink} />;
}
